import { useMemo } from 'react';
import { MeetingNote } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardDataContext } from '../contexts/DashboardDataContext';

export interface CaseAccess {
  /** The case reached this user through a mentorship, not through assignment. */
  isShadowed: (caseId: string) => boolean;
  /** Shadowed and the pairing is finished: visible, nothing writable. */
  isShadowedReadOnly: (caseId: string) => boolean;
  /** Mentor who owns the shadowed case, for the "belongs to" badge. */
  mentorNameFor: (caseId: string) => string | null;
  /** Apprentices attached to a case this user owns, for the observer badge. */
  observersFor: (caseId: string) => string[];
  /** May create a new note on this case. */
  canAddNote: (caseId: string) => boolean;
  /** May edit or delete this specific note. Apprentices only touch their own. */
  canModifyNote: (caseId: string, note: MeetingNote) => boolean;
  /** Everything except notes — case fields, reports, consent, closure. */
  canEditCase: (caseId: string) => boolean;
  /** The consent file is the most sensitive document; apprentices never see it. */
  canSeeConsent: (caseId: string) => boolean;
  /** True when the author is somebody shadowing this case. */
  isNoteByApprentice: (caseId: string, note: MeetingNote) => boolean;
}

/**
 * One place that answers "what may this user do with this case".
 * For anyone without a mentorship every method returns the pre-existing
 * behaviour, so callers can use it unconditionally.
 */
export function useCaseAccess(): CaseAccess {
  const { currentUser } = useAuth();
  const { mentorships, apprenticeCaseIds, apprenticeWritableCaseIds } =
    useDashboardDataContext();

  return useMemo(() => {
    const userId = currentUser?.id || '';
    const isShadowed = (caseId: string) => apprenticeCaseIds.has(caseId);
    const isWritable = (caseId: string) => apprenticeWritableCaseIds.has(caseId);

    const apprenticeIdsFor = (caseId: string) =>
      new Set(
        mentorships
          .filter((m) => m.caseIds.includes(caseId))
          .map((m) => m.apprenticeUserId)
      );

    return {
      isShadowed,
      isShadowedReadOnly: (caseId) => isShadowed(caseId) && !isWritable(caseId),

      mentorNameFor: (caseId) =>
        mentorships.find(
          (m) => m.apprenticeUserId === userId && m.caseIds.includes(caseId)
        )?.mentorName ?? null,

      observersFor: (caseId) =>
        mentorships
          .filter((m) => m.mentorUserId === userId && m.caseIds.includes(caseId))
          .map((m) => m.apprenticeName),

      canAddNote: (caseId) => !isShadowed(caseId) || isWritable(caseId),

      canModifyNote: (caseId, note) => {
        if (!isShadowed(caseId)) return true;
        return isWritable(caseId) && note.createdBy === userId;
      },

      canEditCase: (caseId) => !isShadowed(caseId),
      canSeeConsent: (caseId) => !isShadowed(caseId),

      isNoteByApprentice: (caseId, note) =>
        note.createdBy !== '' && apprenticeIdsFor(caseId).has(note.createdBy),
    };
  }, [currentUser?.id, mentorships, apprenticeCaseIds, apprenticeWritableCaseIds]);
}
